import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useId } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import {
  collapseVariants,
  SECTION_TRANSITION,
  SWAP_TRANSITION,
} from './motion';

export function StepSection({
  index,
  title,
  pill,
  pillTip,
  locked = false,
  lockedTip,
  open,
  onToggle,
  children,
}: {
  index: number;
  title: string;
  pill?: string;
  pillTip?: string;
  locked?: boolean;
  lockedTip: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const reduce = useReducedMotion();
  const contentId = useId();
  const expanded = open && !locked;
  const bodyTransition = reduce ? { duration: 0 } : SECTION_TRANSITION;
  const iconTransition = reduce ? { duration: 0 } : SWAP_TRANSITION;

  return (
    <motion.div
      initial={false}
      animate={{ opacity: locked ? 0.55 : 1 }}
      transition={bodyTransition}
      className={cn(
        'overflow-hidden rounded-lg border bg-surface',
        locked ? 'border-border' : 'border-border-strong shadow-sm'
      )}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => {
          if (locked) return;
          onToggle();
        }}
        className={cn(
          'flex w-full items-center gap-2 px-3 py-2.5 text-left',
          locked ? 'cursor-default' : 'cursor-pointer'
        )}
      >
        <span
          className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded-full font-mono text-[10px] transition-colors',
            locked
              ? 'border border-border-strong text-foreground-muted'
              : 'bg-accent text-accent-foreground'
          )}
        >
          {index}
        </span>
        <span className="text-sm font-medium text-foreground">{title}</span>
        {pill &&
          (pillTip ? (
            <Tooltip label={pillTip} side="top" contentClassName="z-[200]">
              <span className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] text-foreground-muted">
                {pill}
              </span>
            </Tooltip>
          ) : (
            <span className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] text-foreground-muted">
              {pill}
            </span>
          ))}
        <span className="ml-auto flex items-center text-foreground-muted">
          <AnimatePresence mode="wait" initial={false}>
            {locked ? (
              <motion.span
                key="lock"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={iconTransition}
              >
                <Tooltip
                  label={lockedTip}
                  side="top"
                  contentClassName="z-[200]"
                >
                  <Icons.lock className="size-3.5" />
                </Tooltip>
              </motion.span>
            ) : (
              <motion.span
                key="chevron"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={iconTransition}
              >
                <Icons.chevronDown
                  className={cn(
                    'size-4 transition-transform',
                    expanded && 'rotate-180'
                  )}
                />
              </motion.span>
            )}
          </AnimatePresence>
        </span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            id={contentId}
            variants={collapseVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={bodyTransition}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
